import * as React from 'react'
import { Helmet } from 'react-helmet'

import {
  MainContainer,
  ContentSection,
  PageTitle,
  RevisionDate,
  SectionTitle,
  BodyText,
  BoldText,
  List,
  ListItem,
  StyledLink,
  SectionWithLeftMargin,
} from './MainView.style'

export default function MainView() {
  return (
    <>
      <Helmet>
        <title>Event Registration Privacy Notice</title>
        <meta
          name="description"
          content="Ayala Malls Event Registration Privacy Notice - How we protect your personal data in compliance with the Data Privacy Act of 2012."
        />
      </Helmet>
      <MainContainer>
        <ContentSection>
          <PageTitle as="h1">Ayala Malls Event Registration</PageTitle>
          <PageTitle as="h2">Privacy Notice</PageTitle>
          <RevisionDate>Latest Revision: 22-May-2025</RevisionDate>

          <BodyText>
            Welcome to Ayala Malls Event Registration, operated by AyalaLand
            Malls, Inc. (referred to as the &quot;Company&quot;) for itself
            and/or its parent company, affiliates, and related companies owning
            and/or operating Ayala Malls (collectively referred to as
            &quot;Ayala Malls&quot;). In compliance with the Data Privacy Act of
            2012, here&apos;s how Ayala Malls protects your personal data and
            respects your privacy:
          </BodyText>

          <SectionTitle>Our role in your data privacy</SectionTitle>
          <SectionWithLeftMargin>
            <BodyText>
              When you sign up for an Ayala Malls Event (referred to as the
              &quot;Event&quot;), this notice applies to you. Please read and
              understand this notice before providing us with your personal
              data.
            </BodyText>

            <SectionTitle>Our responsibilities</SectionTitle>
            <BodyText>
              We process information pursuant to the Ayala Land Group
              (&quot;Group&quot;) Privacy Statement. To know more about our
              general principles and how we process your personal data within
              the Group, please visit{' '}
              <StyledLink
                href="https://www.ayalaland.com.ph/privacy-statement/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.ayalaland.com.ph/privacy-statement/
              </StyledLink>
              .
            </BodyText>
            <BodyText>
              We may revise this Privacy Notice from time to time as we add
              purposes of data processing or as laws change that may relate to
              or affect the Event. When we make material changes to this Privacy
              Notice, we will post the date it was last revised.
            </BodyText>

            <SectionTitle>Your responsibilities</SectionTitle>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  Read and understand this Privacy Notice and the Terms &amp;
                  Conditions for the Event you are participating in
                </ListItem>
                <ListItem>
                  Ensure that any information that you provide us is complete,
                  accurate and up-to-date
                </ListItem>
                <ListItem>
                  If you provide us with personal data about other people, or if
                  others give us your information, we will use that information
                  only for the specific purpose for which it was provided to us.
                  By submitting the information, you warrant and confirm that
                  you have the right to authorize us to process it on your
                  behalf in accordance with this Privacy Notice.
                </ListItem>
              </List>
            </SectionWithLeftMargin>
          </SectionWithLeftMargin>

          <SectionTitle>When and how we collect data</SectionTitle>
          <SectionWithLeftMargin>
            <BodyText>
              We collect data when you register or pre-register (for both onsite
              and online) for any of our Events.
            </BodyText>
          </SectionWithLeftMargin>

          <SectionTitle>What types of data we collect from you</SectionTitle>
          <BodyText>
            When you register or pre-register for an Event, the Company and/or
            the relevant Ayala Mall collect/s and process/es the following
            personal data that you provide:
          </BodyText>

          <SectionWithLeftMargin>
            <BodyText>
              <BoldText>Personal details</BoldText>
              <br />
              Your first name, last name, email address, mobile number, and city
              of residence.
            </BodyText>

            <BodyText>
              <BoldText>
                Transaction details (for an Event with purchase requirement)
              </BoldText>
              <br />
              Your submitted transactions such as merchant or store name,
              amount, and date of purchase.
            </BodyText>

            <BodyText>
              <BoldText>Event details</BoldText>
              <br />
              Type or category of the event participated in (e.g., Anime &amp;
              Cosplay, Trade Fairs, Fan Nights, etc.).
            </BodyText>
          </SectionWithLeftMargin>

          <SectionTitle>Disclosing personal data other than yours</SectionTitle>
          <BodyText>
            When you disclose to us personal data of another person (e.g., your
            dependent, spouse, children, and/or parents), you attest that
            consent has been obtained from that person to disclose and process
            his/her personal data in accordance with this Privacy Notice.
          </BodyText>
          <BodyText>
            If the personal data was provided by a minor or one who is not
            legally capable of entering into contractual transactions without
            parental or guardian consent, the parent or legal guardian shall be
            responsible in providing the consent, and in correcting any
            information given or informing us that the information should be
            disregarded or deleted.
          </BodyText>

          <SectionTitle>How and why we use your personal data</SectionTitle>
          <BodyText>
            Under the Data Privacy Act, we can only use your personal data for
            certain specified purposes and where we have a legal basis to do so.
            Here are the purposes for which we process your data:
          </BodyText>

          <SectionWithLeftMargin>
            <BodyText>
              <BoldText>Manage your participation in the Event</BoldText>
            </BodyText>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  Process your registration and verify your identity at the
                  Event
                </ListItem>
                <ListItem>
                  Control the number of participants in the Event
                </ListItem>
                <ListItem>Provide Event-related updates</ListItem>
                <ListItem>
                  Facilitate on-site services, check-ins, or giveaways relating
                  to the Event, as applicable
                </ListItem>
              </List>
            </SectionWithLeftMargin>

            <BodyText>
              <BoldText>Personalize your experience with Ayala Malls</BoldText>
            </BodyText>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  Understand your interests and preferences based on the
                  information you provide
                </ListItem>
                <ListItem>
                  Recommend relevant events, activities, stores, services or
                  offers based on your preference
                </ListItem>
                <ListItem>
                  Provide a more seamless and engaging experience across our
                  platforms
                </ListItem>
              </List>
            </SectionWithLeftMargin>

            <BodyText>
              <BoldText>Marketing Communications (With your consent)</BoldText>
            </BodyText>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  Send you emails and notifications about promotional content,
                  upcoming events, special offers, and announcements relating to
                  Ayala Malls
                </ListItem>
                <ListItem>Deliver content based on your preferences</ListItem>
                <ListItem>
                  Invite you to participate in surveys, promotions, raffles, and
                  other marketing initiatives
                </ListItem>
              </List>
            </SectionWithLeftMargin>

            <BodyText>
              <BoldText>Improve our services</BoldText>
            </BodyText>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  Request feedback about your experience in our events or our
                  malls
                </ListItem>
                <ListItem>
                  Processing of information for statistical, analytical and
                  research purposes
                </ListItem>
              </List>
            </SectionWithLeftMargin>
          </SectionWithLeftMargin>

          <SectionTitle>Your privacy rights</SectionTitle>
          <BodyText>
            You can exercise your rights as a data subject by sending us an
            email at{' '}
            <StyledLink href="mailto:ayalamallsdataprivacy@ayalamalls.com.ph">
              ayalamallsdataprivacy@ayalamalls.com.ph
            </StyledLink>
            . When you make a request, note that such a request is subject to
            certain conditions. We may also charge you for any of these
            services.
          </BodyText>

          <SectionWithLeftMargin>
            <BodyText>
              <BoldText>
                You have the right to access information we hold about you
              </BoldText>
              <br />
              You may instruct us to provide you with any personal information
              we hold about you, subject to providing us with appropriate
              evidence of your identity.
            </BodyText>
            <SectionWithLeftMargin>
              <List>
                <ListItem>
                  We may withhold personal information that you request to the
                  extent permitted by law. You may instruct us at any time not
                  to process your personal information by emailing us at
                  <StyledLink href="mailto:ayalamallsdataprivacy@ayalamalls.com.ph">
                    ayalamallsdataprivacy@ayalamalls.com.ph
                  </StyledLink>
                  .
                </ListItem>
                <ListItem>
                  You will be asked to expressly agree in advance to our use of
                  your personal information for marketing purposes, or we will
                  provide you with an opportunity to opt out of the use of your
                  personal information for the same purpose.
                </ListItem>
              </List>
            </SectionWithLeftMargin>

            <BodyText>
              <BoldText>You can object to us using your data.</BoldText>
              <br />
              This includes your right to opt-out of any commercial
              communication from the Company and/or Ayala Malls.
            </BodyText>

            <BodyText>
              <BoldText>
                You have the right to make us correct any inaccurate personal
                data about you
              </BoldText>
              <br />
              We endeavor to process accurate and up-to-date personal data of
              our customers. You have the right to notify us of any inaccurate
              and outdated personal data, and we shall take actions to update
              them accordingly.
            </BodyText>

            <BodyText>
              <BoldText>You have the right to port your data</BoldText>
              <br />
              We will give you a copy of your data in a mutually acceptable
              format. We will not do so to the extent that this involves
              disclosing data about any other individual.
            </BodyText>

            <BodyText>
              <BoldText>You have the right for your data to be erased</BoldText>
              <br />
              You can do this by asking us to withdraw, remove or destroy your
              personal data, if it is no longer necessary for us to hold the
              data and/or in case you decide to no longer participate in the
              Event.
            </BodyText>

            <BodyText>
              <BoldText>
                You have the right to lodge a complaint regarding our use of
                your data
              </BoldText>
              <br />
              Please tell us first, so we have a chance to address your
              concerns. If we fail in this, you can address any complaint to the
              National Privacy Commission.
            </BodyText>

            <BodyText>
              <BoldText>You have the right to damages</BoldText>
              <br />
              You have the right to damages arising from inaccurate, incomplete,
              false, unlawfully obtained or unauthorized use of your personal
              data by the Company.
            </BodyText>
          </SectionWithLeftMargin>

          <SectionTitle>How secure is the data we collect?</SectionTitle>
          <BodyText>
            We do our best to ensure that your personal data is protected and
            your privacy is preserved.
          </BodyText>
          <BodyText>
            We use physical, technical, and organizational safeguards to protect
            your personal data against loss, theft, and unauthorized access,
            disclosure, copying, use, or modification. These practices include:
          </BodyText>
          <SectionWithLeftMargin>
            <List $triangle>
              <ListItem>
                Use of secured servers behind advanced threat protection
                appliances, firewalls, encryption and other security tools.
              </ListItem>
              <ListItem>
                Use of standard encryption tools such as SSL (Secure Socket
                Layer) to help protect personal data and to keep information
                collected secure.
              </ListItem>
              <ListItem>
                Website/Application security testing conducted by a third party
                prior to it being launched and when major changes have been
                applied.
              </ListItem>
            </List>
          </SectionWithLeftMargin>
          <BodyText>
            In addition, we limit access to your personal data to those
            employees, agents, contractors and other third parties who are
            qualified and authorized to process them. All such persons, wherever
            they are located, are required to protect the confidentiality and
            privacy of your personal data in a manner consistent with our
            privacy policies and practices.
          </BodyText>
          <BodyText>
            <BoldText>And please remember:</BoldText>
          </BodyText>
          <SectionWithLeftMargin>
            <List>
              <ListItem>
                You provide personal data at your own risk. Unfortunately, no
                data transmission is guaranteed to be 100% secure.
              </ListItem>
              <ListItem>
                If you believe your privacy has been breached, please contact us
                immediately at{' '}
                <StyledLink href="mailto:ayalamallsdataprivacy@ayalamalls.com.ph">
                  ayalamallsdataprivacy@ayalamalls.com.ph
                </StyledLink>
                .
              </ListItem>
            </List>
          </SectionWithLeftMargin>

          <SectionTitle>How long do we store your data?</SectionTitle>
          <BodyText>
            We shall retain your personal data obtained via the registration to
            the Event for a period of ten (10) years from the fulfillment of the
            declared purposes or allowable criteria for its processing; for such
            period as we may be required to do so by applicable law, if any; or,
            for such period that your personal data may be relevant to any
            ongoing or prospective legal proceedings in order to establish,
            exercise, and defend our legal rights (including providing
            information to others for the purposes of fraud prevention and
            reducing credit risk).
          </BodyText>
          <BodyText>
            Thereafter, your personal data shall be disposed or discarded in a
            secure manner that would prevent further processing, unauthorized
            access, or disclosure to any other party or the public.
            Specifically, the personal information you have provided
            specifically for the Event will be deleted from our third-party
            database, following the retention period stated above.
          </BodyText>

          <SectionTitle>Third parties who process your data</SectionTitle>
          <BodyText>
            <BoldText>Fulfillment of Legitimate and Business Purposes</BoldText>
            <br />
            In fulfillment of the stated purpose/s that you consented to, we may
            disclose your personal information to the following parties:
          </BodyText>
          <List $triangle>
            <ListItem>
              Authorized third-party service providers including administration
              service processors, technology partners, and Customer Relationship
              Management system (HubSpot Asia Pte Ltd.)
            </ListItem>
            <ListItem>Our employees and officers</ListItem>
            <ListItem>
              Members of the Ayala Malls group of companies or the
              Company&apos;s subsidiaries and affiliates
            </ListItem>
            <ListItem>
              Our holding company Ayala Land Inc. and all its subsidiaries and
              affiliates
            </ListItem>
            <ListItem>
              Professional advisers such as lawyers and auditors
            </ListItem>
            <ListItem>
              Such other party whom you authorize us to disclose your personal
              data.
            </ListItem>
          </List>

          <BodyText>
            In order to comply with legal and statutory compliance, we may
            disclose your personal data:
          </BodyText>
          <List $triangle>
            <ListItem>
              To the extent that we are required to do so by applicable laws,
              rules, and regulations;
            </ListItem>
            <ListItem>
              in connection with any ongoing or prospective legal proceeding;
            </ListItem>
            <ListItem>
              to any person who we reasonably believe may apply to a court or
              other competent authority for disclosure of such personal data
              where such court or authority would be reasonably and likely to
              order the disclosure of such personal data.
            </ListItem>
          </List>

          <BodyText>
            Any company which we share information about you for the above
            purposes is or will be contractually required to comply with
            confidentiality standards, undertake to respect any
            individual&apos;s right to privacy, require our consent for any
            further processing of your personal data by their own third-party
            services providers (as may be applicable), and comply with the Data
            Privacy Act. We also require that these organizations use this
            information only for our purposes and follow our reasonable
            directions concerning this information. This includes organizations
            that handle or obtain personal data as service providers for us,
            who, in addition to the above measures, undergo our due diligence
            that includes their data protection practices.
          </BodyText>

          <SectionTitle>How to Contact Us</SectionTitle>
          <BodyText>
            For comments, questions, or requests relating to this privacy
            notice, or complaints in relation to your rights under this privacy
            notice, you may contact us at:
          </BodyText>
          <BodyText>
            AyalaLand Malls, Inc.
            <br />
            <BoldText>Email address: </BoldText>
            <br />
            <StyledLink href="mailto:ayalamallsdataprivacy@ayalamalls.com.ph">
              ayalamallsdataprivacy@ayalamalls.com.ph
            </StyledLink>
          </BodyText>
        </ContentSection>
      </MainContainer>
    </>
  )
}
